import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Helmet } from 'react-helmet-async';

const COLORS = [
    { bg: 'rgba(52,211,153,0.10)',  from: '#34D399', to: '#3B82F6', glow: 'rgba(52,211,153,0.35)',  text: '#34D399' },
    { bg: 'rgba(139,92,246,0.10)',  from: '#8B5CF6', to: '#EC4899', glow: 'rgba(139,92,246,0.35)',  text: '#a78bfa' },
    { bg: 'rgba(245,158,11,0.10)',  from: '#F59E0B', to: '#EF4444', glow: 'rgba(245,158,11,0.35)',  text: '#FCD34D' },
    { bg: 'rgba(59,130,246,0.10)',  from: '#3B82F6', to: '#06B6D4', glow: 'rgba(59,130,246,0.35)',  text: '#60a5fa' },
    { bg: 'rgba(236,72,153,0.10)',  from: '#EC4899', to: '#8B5CF6', glow: 'rgba(236,72,153,0.35)',  text: '#f472b6' },
    { bg: 'rgba(16,185,129,0.10)',  from: '#10B981', to: '#34D399', glow: 'rgba(16,185,129,0.35)',  text: '#34D399' },
    { bg: 'rgba(239,68,68,0.10)',   from: '#EF4444', to: '#F59E0B', glow: 'rgba(239,68,68,0.35)',   text: '#f87171' },
    { bg: 'rgba(6,182,212,0.10)',   from: '#06B6D4', to: '#3B82F6', glow: 'rgba(6,182,212,0.35)',   text: '#22d3ee' },
    { bg: 'rgba(251,191,36,0.10)',  from: '#FBBF24', to: '#10B981', glow: 'rgba(251,191,36,0.35)',  text: '#fde68a' },
];

export default function Calendar() {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [showLive, setShowLive] = useState(false);
    const [loading, setLoading]   = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
    const canvasRef = useRef(null);

    useEffect(() => {
        fetch('https://onlineico.com/api/calendar.php')
            .then(r => r.json())
            .then(data => {
                setShowLive(data.show_live || false);
                setProjects(Array.isArray(data.projects) ? data.projects : []);
                setLoading(false);
            });

        const onResize = () => setIsMobile(window.innerWidth < 600);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // ── Starfield canvas ──
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');

        const resize = () => {
            canvas.width  = window.innerWidth;
            canvas.height = canvas.parentElement.offsetHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        // ── 1. Étoiles ──
        const stars = Array.from({ length: 220 }, () => ({
            x:     Math.random(), y: Math.random(),
            r:     Math.random() * 1.2 + 0.3,
            speed: Math.random() * 0.8 + 0.4,
            phase: Math.random() * Math.PI * 2,
            color: Math.random() > 0.85 ? 'rgba(150,180,255,' : Math.random() > 0.7 ? 'rgba(255,220,180,' : 'rgba(255,255,255,',
        }));

        // ── 2. Nébuleuses ──
        const nebulae = [
            { x: 0.12, y: 0.20, r: 420, color: '100,80,220',  speed: 0.3,  phase: 0.0 },
            { x: 0.78, y: 0.55, r: 460, color: '0,110,210',   speed: 0.18, phase: 1.5 },
            { x: 0.48, y: 0.88, r: 370, color: '170,50,210',  speed: 0.28, phase: 3.1 },
            { x: 0.88, y: 0.15, r: 340, color: '0,180,140',   speed: 0.22, phase: 2.0 },
        ];

        // ── 3. Comètes ──
        let comets = [];
        let lastCometTime = -99999;
        let nextCometDelay = 9000 + Math.random() * 6000;

        const spawnComet = (t) => {
            const fromTop = Math.random() > 0.4;
            const x = fromTop ? Math.random() * canvas.width : -20;
            const y = fromTop ? -20 : Math.random() * canvas.height * 0.5;
            const angle = fromTop
                ? Math.PI / 4 + Math.random() * Math.PI / 6
                : -(Math.PI / 8) + Math.random() * (Math.PI / 4);
            const speed = 3.5 + Math.random() * 2.5;
            comets.push({
                x, y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                length: 130 + Math.random() * 90,
                alpha: 1,
                color: Math.random() > 0.5 ? '255,255,255' : '180,215,255',
            });
            lastCometTime  = t;
            nextCometDelay = 9000 + Math.random() * 6000;
        };

        // ── 4. Étoiles filantes (rafales) ──
        let shootingStars = [];
        let lastBurstTime  = -99999;
        let nextBurstDelay = 18000 + Math.random() * 20000;

        const spawnBurst = (t) => {
            const count = 2 + Math.floor(Math.random() * 3);
            for (let i = 0; i < count; i++) {
                setTimeout(() => {
                    shootingStars.push({
                        x: Math.random() * canvas.width * 0.65,
                        y: Math.random() * canvas.height * 0.45,
                        vx: 9 + Math.random() * 7,
                        vy: 3 + Math.random() * 5,
                        length: 55 + Math.random() * 65,
                        alpha: 1,
                    });
                }, i * (80 + Math.random() * 180));
            }
            lastBurstTime  = t;
            nextBurstDelay = 18000 + Math.random() * 20000;
        };

        let animId;
        const draw = (t) => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Nébuleuses
            nebulae.forEach(n => {
                const op = 0.09 + 0.05 * Math.sin(t * 0.0005 * n.speed + n.phase);
                const g  = ctx.createRadialGradient(
                    n.x * canvas.width, n.y * canvas.height, 0,
                    n.x * canvas.width, n.y * canvas.height, n.r
                );
                g.addColorStop(0, `rgba(${n.color},${op.toFixed(3)})`);
                g.addColorStop(1, `rgba(${n.color},0)`);
                ctx.fillStyle = g;
                ctx.beginPath();
                ctx.arc(n.x * canvas.width, n.y * canvas.height, n.r, 0, Math.PI * 2);
                ctx.fill();
            });

            // Étoiles
            stars.forEach(s => {
                const op = 0.2 + 0.75 * (0.5 + 0.5 * Math.sin(t * 0.001 * s.speed + s.phase));
                ctx.beginPath();
                ctx.arc(s.x * canvas.width, s.y * canvas.height, s.r, 0, Math.PI * 2);
                ctx.fillStyle = `${s.color}${op.toFixed(2)})`;
                ctx.fill();
            });

            // Comètes
            if (t - lastCometTime > nextCometDelay) spawnComet(t);
            comets = comets.filter(c => c.alpha > 0.02 && c.x < canvas.width + 300 && c.y < canvas.height + 300);
            comets.forEach(c => {
                c.x += c.vx; c.y += c.vy; c.alpha *= 0.987;
                const ang = Math.atan2(c.vy, c.vx);
                const g   = ctx.createLinearGradient(c.x, c.y, c.x - Math.cos(ang) * c.length, c.y - Math.sin(ang) * c.length);
                g.addColorStop(0,   `rgba(${c.color},${(c.alpha * 0.92).toFixed(2)})`);
                g.addColorStop(0.35,`rgba(${c.color},${(c.alpha * 0.28).toFixed(2)})`);
                g.addColorStop(1,   `rgba(${c.color},0)`);
                ctx.beginPath();
                ctx.moveTo(c.x, c.y);
                ctx.lineTo(c.x - Math.cos(ang) * c.length, c.y - Math.sin(ang) * c.length);
                ctx.strokeStyle = g; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.stroke();
                // Tête brillante
                ctx.beginPath();
                ctx.arc(c.x, c.y, 2.8, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(255,255,255,${(c.alpha * 0.95).toFixed(2)})`;
                ctx.fill();
                // Halo autour de la tête
                const halo = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, 10);
                halo.addColorStop(0, `rgba(200,225,255,${(c.alpha * 0.35).toFixed(2)})`);
                halo.addColorStop(1, `rgba(200,225,255,0)`);
                ctx.fillStyle = halo;
                ctx.beginPath();
                ctx.arc(c.x, c.y, 10, 0, Math.PI * 2);
                ctx.fill();
            });

            // Étoiles filantes
            if (t - lastBurstTime > nextBurstDelay) spawnBurst(t);
            shootingStars = shootingStars.filter(s => s.alpha > 0.04);
            shootingStars.forEach(s => {
                s.x += s.vx; s.y += s.vy; s.alpha *= 0.91;
                const ang = Math.atan2(s.vy, s.vx);
                const g   = ctx.createLinearGradient(s.x, s.y, s.x - Math.cos(ang) * s.length, s.y - Math.sin(ang) * s.length);
                g.addColorStop(0, `rgba(255,255,255,${(s.alpha * 0.9).toFixed(2)})`);
                g.addColorStop(1, `rgba(255,255,255,0)`);
                ctx.beginPath();
                ctx.moveTo(s.x, s.y);
                ctx.lineTo(s.x - Math.cos(ang) * s.length, s.y - Math.sin(ang) * s.length);
                ctx.strokeStyle = g; ctx.lineWidth = 1.2; ctx.lineCap = 'round'; ctx.stroke();
            });

            animId = requestAnimationFrame(draw);
        };
        animId = requestAnimationFrame(draw);

        return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
    }, []);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const getDaysUntil = (dateStr) => {
        const d = new Date(dateStr);
        d.setHours(0, 0, 0, 0);
        return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
    };

    const getBubbleSize = (days) => {
        const scale = isMobile ? 0.72 : 1;
        let base;
        if (days <= 3)  base = 200;
        else if (days <= 7)  base = 172;
        else if (days <= 14) base = 145;
        else if (days <= 30) base = 118;
        else if (days <= 60) base = 95;
        else base = 78;
        return Math.round(base * scale);
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`;
    };

    // Offset vertical pseudo-aléatoire stable par projet (effet "libre")
    const getOffset = (id) => (id * 23 + 11) % 38;

    return (
        <div className="app-wrap" style={{ background: '#020408' }}>

<Helmet>
    <title>ICO & IDO Calendar | Live & Upcoming Presales | OnlineICO</title>
    <meta name="description" content="Track live and upcoming ICO, IDO & crypto presales. Filter by date, blockchain and status. Find the next crypto gem before launch." />
    <link rel="canonical" href="https://onlineico.com/" />
    <meta property="og:type" content="website" />
    <meta property="og:site_name" content="OnlineICO" />
    <meta property="og:title" content="ICO & IDO Calendar | Live & Upcoming Presales | OnlineICO" />
    <meta property="og:description" content="Track live and upcoming ICO, IDO & crypto presales. Filter by date, blockchain and status. Find the next crypto gem before launch." />
    <meta property="og:url" content="https://onlineico.com/" />
    <meta property="og:image" content="https://onlineico.com/og-image.png" />
    <meta property="og:image:width" content="1200" />
    <meta property="og:image:height" content="630" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:site" content="@online_ico" />
    <meta name="twitter:title" content="ICO & IDO Calendar | Live & Upcoming Presales | OnlineICO" />
    <meta name="twitter:description" content="Track live and upcoming ICO, IDO & crypto presales. Filter by date, blockchain and status. Find the next crypto gem before launch." />
    <meta name="twitter:image" content="https://onlineico.com/og-image.png" />

<script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "OnlineICO",
        "url": "https://onlineico.com",
        "description": "Discover the best ICO, IDO and crypto presales. Vote for projects, earn token rewards by completing quests, or launch your token with Token Studio.",
        "publisher": {
            "@type": "Organization",
            "name": "OnlineICO",
            "url": "https://onlineico.com",
            "logo": "https://onlineico.com/logo_onlineico_v.png"
        },
        "potentialAction": {
            "@type": "SearchAction",
            "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://onlineico.com/ico/{search_term_string}"
            },
            "query-input": "required name=search_term_string"
        }
    })}</script>

</Helmet>


            <Navbar />

            <style>{`
                @keyframes ico-float {
                    0%, 100% { transform: translateY(0px); }
                    50%       { transform: translateY(-14px); }
                }
                .ico-bubble:hover { transform: scale(1.07) !important; }
            `}</style>

            <div style={{ position: 'relative', minHeight: '100vh' }}>
            <canvas ref={canvasRef} style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                pointerEvents: 'none', zIndex: 0,
            }} />
            <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '48px 20px 100px', position: 'relative', zIndex: 1 }}>

                {/* ── Header ── */}
                <div style={{ textAlign: 'center', marginBottom: '64px' }}>
                    <div style={{ fontSize: '0.72rem', color: '#34D399', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '12px' }}>
                        {showLive ? 'Live & Upcoming presales' : 'Upcoming presales'}
                    </div>
                    <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: '800', fontSize: 'clamp(2rem, 5vw, 3.2rem)', color: '#F1F5F9', margin: 0, lineHeight: 1.1 }}>
                        ICO Calendar
                    </h1>
                    <p style={{ color: '#64748b', marginTop: '14px', fontSize: '0.88rem' }}>
                        The larger the bubble, the sooner the presale starts.
                    </p>
                </div>

                {/* ── Contenu ── */}
                {loading ? (
                    <div style={{ textAlign: 'center', color: '#64748b', padding: '5rem', fontSize: '0.9rem' }}>Loading...</div>
                ) : projects.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#64748b', padding: '5rem' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🗓</div>
                        <div>No upcoming presales at the moment.</div>
                    </div>
                ) : (
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        gap: isMobile ? '16px' : '28px',
                        padding: '10px 0 40px',
                    }}>
                        {projects.map((p, i) => {
                            const days    = getDaysUntil(p.presale_start);
                            const size    = getBubbleSize(days);
                            const hasCredentials = p.kyc || p.audit;
                            const color   = hasCredentials ? COLORS[i % COLORS.length] : null;

                            // Bordure dégradée
                            const gradientBorder = hasCredentials
                                ? `linear-gradient(rgba(11,17,32,0.92), rgba(11,17,32,0.92)) padding-box, linear-gradient(135deg, ${color.from}, ${color.to}, ${color.from}) border-box`
                                : `linear-gradient(rgba(51,65,85,0.18), rgba(30,41,59,0.25)) padding-box, linear-gradient(135deg, #475569, #64748b, #334155) border-box`;

                            // Animation désynchronisée : durée et délai uniques par projet
                            const floatDuration = (2.8 + (p.id * 17 % 28) / 10).toFixed(1); // 2.8s à 5.6s
                            const floatDelay    = -((p.id * 13 % 35) / 10).toFixed(1);       // 0s à -3.5s (déjà en cours)
                            const floatAmp      = days <= 7 ? 14 : days <= 14 ? 9 : 0;
                            const floatAnim     = floatAmp > 0
                                ? `ico-float ${floatDuration}s ${floatDelay}s ease-in-out infinite`
                                : 'none';

                            const offset  = isMobile ? 0 : getOffset(p.id);
                            const isUrgent = days <= 7;
                            const nameLen = p.name.length;
                            const fsBase  = size >= 145 ? 0.82 : size >= 115 ? 0.74 : 0.65;
                            const fsScale = nameLen > 10 ? 0.88 : nameLen > 8 ? 0.93 : 1;
                            const fs      = `${(fsBase * fsScale).toFixed(2)}rem`;

                            const glow     = hasCredentials ? color.glow : 'rgba(255,255,255,0.06)';
                            const glowHover = hasCredentials ? color.glow : 'rgba(255,255,255,0.10)';

                            const accentColor = hasCredentials ? color.from : '#475569';

                            return (
                                <div
                                    key={p.id}
                                    className="ico-bubble"
                                    onClick={() => navigate(`/${p.type}/${p.slug}`)}
                                    style={{
                                        width:          `${size}px`,
                                        height:         `${size}px`,
                                        borderRadius:   '50%',
                                        background:     gradientBorder,
                                        border:         '4px solid transparent',
                                        boxShadow:      isUrgent
                                            ? `0 0 30px ${glow}, 0 0 60px ${glow}, inset 0 -8px 20px rgba(0,0,0,0.3)`
                                            : `0 0 14px ${glow}, inset 0 -8px 20px rgba(0,0,0,0.25)`,
                                        position:       'relative',
                                        overflow:       'hidden',
                                        cursor:         'pointer',
                                        marginTop:      `${offset}px`,
                                        flexShrink:     0,
                                        animation:      floatAnim,
                                        transition:     'transform 0.25s ease, box-shadow 0.25s ease',
                                    }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.boxShadow = `0 0 50px ${glowHover}, 0 0 100px ${glowHover}, inset 0 -8px 20px rgba(0,0,0,0.3)`;
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.boxShadow = isUrgent
                                            ? `0 0 30px ${glow}, 0 0 60px ${glow}, inset 0 -8px 20px rgba(0,0,0,0.3)`
                                            : `0 0 14px ${glow}, inset 0 -8px 20px rgba(0,0,0,0.25)`;
                                    }}
                                >
                                    {/* ── Bordure intérieure dégradée ── */}
                                    <div style={{
                                        position: 'absolute', inset: '6px',
                                        borderRadius: '50%',
                                        border: '2px solid transparent',
                                        background: hasCredentials
                                            ? `linear-gradient(rgba(11,17,32,0), rgba(11,17,32,0)) padding-box, linear-gradient(135deg, rgba(255,255,255,0.35), ${color.from}88, rgba(255,255,255,0.08), ${color.to}55) border-box`
                                            : `linear-gradient(rgba(11,17,32,0), rgba(11,17,32,0)) padding-box, linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0.03)) border-box`,
                                        pointerEvents: 'none',
                                    }} />

                                    {/* ── Reflet principal haut-gauche ── */}
                                    <div style={{
                                        position: 'absolute', top: '10%', left: '14%',
                                        width: '42%', height: '28%',
                                        background: 'radial-gradient(ellipse at 40% 40%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 75%)',
                                        borderRadius: '50%',
                                        transform: 'rotate(-30deg)',
                                        pointerEvents: 'none',
                                    }} />

                                    {/* ── Reflet secondaire bas-droite (subtil) ── */}
                                    <div style={{
                                        position: 'absolute', bottom: '12%', right: '12%',
                                        width: '28%', height: '18%',
                                        background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 70%)',
                                        borderRadius: '50%',
                                        pointerEvents: 'none',
                                    }} />

                                    {/* ── Teinte intérieure colorée ── */}
                                    <div style={{
                                        position: 'absolute', inset: 0,
                                        borderRadius: '50%',
                                        background: hasCredentials
                                            ? `radial-gradient(circle at 60% 65%, ${color.from}18 0%, transparent 70%)`
                                            : 'radial-gradient(circle at 60% 65%, rgba(71,85,105,0.35) 0%, rgba(51,65,85,0.15) 70%)',
                                        pointerEvents: 'none',
                                    }} />

                                    {/* ── Contenu centré ── */}
                                    <div style={{
                                        position: 'relative', zIndex: 1,
                                        display: 'flex', flexDirection: 'column',
                                        alignItems: 'center', justifyContent: 'center',
                                        textAlign: 'center', padding: '14px', width: '100%', height: '100%',
                                    }}>
                                        {/* Nom */}
                                        <div style={{
                                            color: hasCredentials ? '#F1F5F9' : '#475569',
                                            fontFamily: 'Syne, sans-serif', fontWeight: '800',
                                            fontSize: fs, lineHeight: 1.2, marginBottom: '4px',
                                            wordBreak: 'break-word', maxWidth: '80%',
                                            textShadow: hasCredentials ? `0 0 12px ${accentColor}66` : 'none',
                                        }}>
                                            {p.name}
                                        </div>

                                        {/* Catégorie */}
                                        <div style={{
                                            color: hasCredentials ? '#34D399' : '#334155', fontWeight: '600',
                                            fontSize: `calc(${fs} - 0.07rem)`,
                                            textTransform: 'uppercase', letterSpacing: '0.5px',
                                            marginBottom: '3px',
                                        }}>
                                            {p.type}
                                        </div>

                                        {/* Date */}
                                        <div style={{ color: hasCredentials ? '#34D399' : '#334155', fontSize: `calc(${fs} - 0.09rem)` }}>
                                            {formatDate(p.presale_start)}
                                        </div>

                                        {/* Badge Live */}
                                        {p.calendar_status === 'live' && (
                                            <div style={{
                                                marginTop: '5px',
                                                background: 'rgba(52,211,153,0.15)',
                                                border: '1px solid rgba(52,211,153,0.5)',
                                                borderRadius: '20px', padding: '1px 7px',
                                                fontSize: '0.6rem', color: '#34D399', fontWeight: '800',
                                                letterSpacing: '0.5px',
                                            }}>
                                                🟢 LIVE
                                            </div>
                                        )}

                                        {/* Badge urgence upcoming */}
                                        {p.calendar_status !== 'live' && isUrgent && (
                                            <div style={{
                                                marginTop: '5px',
                                                background: `${accentColor}22`,
                                                border: `1px solid ${accentColor}55`,
                                                borderRadius: '20px', padding: '1px 7px',
                                                fontSize: '0.6rem', color: hasCredentials ? color.text : '#64748b',
                                                fontWeight: '800', letterSpacing: '0.5px',
                                            }}>
                                                {days === 0 ? 'TODAY' : `${days}d left`}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            </div>
        </div>
    );
}
